import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, CreditCard, User,
  MapPin, Briefcase, Shield, FileText
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { formatDate, todayInputDate } from '../../utils/dateUtils';

const PAYMENT_MODES = [
  'Cash', 'Cheque', 'NEFT', 'RTGS', 'IMPS',
  'UPI', 'Demand Draft', 'Online Transfer'
];

const statusBadge = {
  PENDING: 'pending', REFERRAL_PENDING: 'pending',
  REFERRAL_APPROVED: 'approved', PAYMENT_PENDING: 'pending',
  PAYMENT_RECEIVED: 'payment', ACTIVE: 'active', REJECTED: 'rejected',
};

export default function AdminMemberDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentForm, setPaymentForm] = useState({
    receivedAmount: '', paymentDate: '',
    modeOfPayment: '', referenceNumber: '', remarks: ''
  });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [activateLoading, setActivateLoading] = useState(false);

  const fetchMember = async () => {
    try {
      const res = await api.get(`/admin/members/${id}`);
      setMember(res.data.member);
      if (res.data.member.payment) {
        const p = res.data.member.payment;
        setPaymentForm({
          receivedAmount: p.receivedAmount || '',
          paymentDate: p.paymentDate ? p.paymentDate.split('T')[0] : '',
          modeOfPayment: p.modeOfPayment || '',
          referenceNumber: p.referenceNumber || '',
          remarks: p.remarks || '',
        });
      }
    } catch {
      toast.error('Failed to fetch member details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMember(); }, [id]);

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.modeOfPayment) { toast.error('Please select payment mode'); return; }
    setPaymentLoading(true);
    try {
      await api.post(`/admin/members/${id}/payment`, paymentForm);
      toast.success('Payment recorded successfully!');
      fetchMember();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleActivate = async () => {
    if (member?.status !== 'PAYMENT_RECEIVED') {
      toast.error('Payment must be recorded before activating the member');
      return;
    }
    const result = await Swal.fire({
      title: 'Confirm Activation',
      text: `Activate ${member.user.firstName} ${member.user.lastName}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Activate',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#6b7280',
      width: '360px', padding: '1.2rem',
    });
    if (!result.isConfirmed) return;
    setActivateLoading(true);
    try {
      await api.post(`/admin/members/${id}/activate`);
      await Swal.fire({
        title: 'Activated!',
        text: 'Member account activated successfully. Welcome email sent.',
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#1a3a5c',
        width: '360px', padding: '1.2rem',
      });
      fetchMember();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to activate member');
    } finally {
      setActivateLoading(false);
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!member) return <div>Member not found</div>;

  const fullName = `${member.user.firstName} ${member.user.middleName || ''} ${member.user.lastName}`.trim();
  const canRecordPayment = ['REFERRAL_APPROVED', 'PAYMENT_PENDING', 'PAYMENT_RECEIVED'].includes(member.status);
  const canActivate = member.status === 'PAYMENT_RECEIVED';
  const isActive = member.status === 'ACTIVE';

  const Row = ({ label, value }) => value ? (
    <div style={{ display: 'flex', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ width: 180, color: 'var(--text-muted)', fontSize: 13, flexShrink: 0 }}>{label}</div>
      <div style={{ color: 'var(--text)', fontSize: 14 }}>{value}</div>
    </div>
  ) : null;

  let scannedDegrees = [];
  try {
    if (member.scannedDegrees) scannedDegrees = JSON.parse(member.scannedDegrees);
  } catch { scannedDegrees = []; }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/admin/members')}>
          <ArrowLeft size={14} /> Back
        </button>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--primary)', fontSize: 22 }}>
            {fullName}
          </h2>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
            <span style={{ fontWeight: 700, color: 'var(--primary)', fontSize: 13 }}>{member.memberId}</span>
            <span className={`badge badge-${statusBadge[member.status] || 'pending'}`}>
              {member.status.replace(/_/g, ' ')}
            </span>
            {member.membershipType && (
              <span style={{
                background: '#dbeafe', color: '#1e40af',
                padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600
              }}>
                {member.membershipType}
              </span>
            )}
          </div>
        </div>
        {isActive && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: '#dcfce7', padding: '8px 14px', borderRadius: 8,
            fontSize: 13, color: '#14532d', fontWeight: 600
          }}>
            <CheckCircle size={14} /> Active Member
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Left Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Personal Info */}
          <div className="card" style={{ padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%', overflow: 'hidden',
                background: member.profilePhoto ? 'transparent' : 'var(--primary)',
                border: '2px solid var(--border)', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {member.profilePhoto ? (
                  <img src={`https://demo11.stsgram.com/${member.profilePhoto}`}
                    alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ color: 'white', fontWeight: 700, fontSize: 18 }}>
                    {member.user.firstName?.[0]}{member.user.lastName?.[0]}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <User size={17} style={{ color: 'var(--primary)' }} />
                <h3 style={{ fontSize: 15, color: 'var(--primary)' }}>Personal Information</h3>
              </div>
            </div>
            <Row label="Full Name" value={fullName} />
            <Row label="Email" value={member.user.email} />
            <Row label="Mobile" value={member.user.mobile} />
            {/* DD/MM/YYYY format */}
            <Row label="Date of Birth" value={formatDate(member.dateOfBirth)} />
            <Row label="Age" value={member.age ? `${member.age} years` : null} />
            <Row label="Gender" value={member.gender} />
            <Row label="Nationality" value={member.nationality} />
            {/* DD/MM/YYYY format */}
            <Row label="Registered On" value={formatDate(member.createdAt)} />
          </div>

          {/* Contact */}
          <div className="card" style={{ padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <MapPin size={17} style={{ color: 'var(--primary)' }} />
              <h3 style={{ fontSize: 15, color: 'var(--primary)' }}>Contact Information</h3>
            </div>
            <Row label="Residential Address" value={member.address} />
            <Row label="City" value={member.city} />
            <Row label="State" value={member.state} />
            <Row label="Pin Code" value={member.pincode} />
            <Row label="Phone" value={member.phone} />
          </div>

          {/* Professional */}
          <div className="card" style={{ padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <Briefcase size={17} style={{ color: 'var(--primary)' }} />
              <h3 style={{ fontSize: 15, color: 'var(--primary)' }}>Professional Information</h3>
            </div>
            <Row label="Degree" value={member.degree} />
            <Row label="University" value={member.university} />
            <Row label="Year of Passing" value={member.yearOfPassing} />
            <Row label="Areas of Expertise" value={member.areasOfInterest} />
            <Row label="Clinic / Practice Address" value={member.officeAddress} />
            <Row label="Membership Type" value={member.membershipType} />
          </div>

          {/* Scanned Documents */}
          {scannedDegrees.length > 0 && (
            <div className="card" style={{ padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <FileText size={17} style={{ color: 'var(--primary)' }} />
                <h3 style={{ fontSize: 15, color: 'var(--primary)' }}>Uploaded Documents</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {scannedDegrees.map((doc, idx) => (
                  <a key={idx} href={`https://demo11.stsgram.com${doc}`}
                    target="_blank" rel="noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', background: '#dbeafe',
                      borderRadius: 8, textDecoration: 'none',
                      color: 'var(--primary)', fontSize: 13, fontWeight: 500
                    }}>
                    📄 Document {idx + 1} — Click to View
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Referral Status */}
          <div className="card" style={{ padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <Shield size={17} style={{ color: 'var(--primary)' }} />
              <h3 style={{ fontSize: 15, color: 'var(--primary)' }}>Referral Status</h3>
            </div>
            {member.referralApprovals?.map((approval) => (
              <div key={approval.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>Referral {approval.referralNumber}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      {approval.referralUser?.firstName} {approval.referralUser?.lastName}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {approval.referralUser?.email}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span className={`badge badge-${approval.approved ? 'active' : 'pending'}`}>
                      {approval.approved ? '✓ Approved' : 'Pending'}
                    </span>
                    {/* DD/MM/YYYY format */}
                    {approval.approvedAt && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                        {formatDate(approval.approvedAt)}
                      </div>
                    )}
                  </div>
                </div>
                {approval.remarks && (
                  <div style={{
                    fontSize: 12, color: 'var(--text-muted)', marginTop: 6,
                    background: 'var(--bg)', padding: '6px 10px', borderRadius: 6
                  }}>
                    Remarks: {approval.remarks}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Payment Section */}
          {canRecordPayment || member.payment ? (
            <div className="card" style={{ padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <CreditCard size={17} style={{ color: 'var(--primary)' }} />
                <h3 style={{ fontSize: 15, color: 'var(--primary)' }}>Payment Details</h3>
                {isActive && (
                  <span style={{
                    background: '#dcfce7', color: '#14532d',
                    fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600
                  }}>Read Only</span>
                )}
              </div>

              <form onSubmit={handleRecordPayment} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">Amount Received (₹) *</label>
                  <input type="number" className="form-input"
                    value={paymentForm.receivedAmount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, receivedAmount: e.target.value })}
                    placeholder="e.g. 500" required min={0}
                    disabled={isActive}
                    style={{ background: isActive ? '#f8fafc' : 'white' }} />
                </div>

                <div className="form-group">
                  <label className="form-label">Payment Date * (DD/MM/YYYY)</label>
                  <input type="date" className="form-input"
                    value={paymentForm.paymentDate}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                    required max={todayInputDate()}
                    disabled={isActive}
                    style={{ background: isActive ? '#f8fafc' : 'white' }} />
                  {paymentForm.paymentDate && (
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      Selected: {formatDate(paymentForm.paymentDate)}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Mode of Payment *</label>
                  <select className="form-input"
                    value={paymentForm.modeOfPayment}
                    onChange={(e) => setPaymentForm({ ...paymentForm, modeOfPayment: e.target.value })}
                    required disabled={isActive}
                    style={{ background: isActive ? '#f8fafc' : 'white' }}>
                    <option value="">-- Select Payment Mode --</option>
                    {PAYMENT_MODES.map((mode) => (
                      <option key={mode} value={mode}>{mode}</option>
                    ))}
                  </select>
                </div>

                {/* Dynamic Reference Number */}
                {paymentForm.modeOfPayment && paymentForm.modeOfPayment !== 'Cash' && (
                  <div className="form-group">
                    <label className="form-label">
                      {paymentForm.modeOfPayment === 'Cheque' && 'Cheque Number *'}
                      {paymentForm.modeOfPayment === 'Demand Draft' && 'DD Number *'}
                      {['UPI', 'NEFT', 'RTGS', 'IMPS', 'Online Transfer'].includes(paymentForm.modeOfPayment)
                        && 'Transaction Reference Number *'}
                    </label>
                    <input className="form-input"
                      value={paymentForm.referenceNumber}
                      onChange={(e) => setPaymentForm({ ...paymentForm, referenceNumber: e.target.value })}
                      placeholder={
                        paymentForm.modeOfPayment === 'Cheque' ? 'Enter cheque number' :
                        paymentForm.modeOfPayment === 'Demand Draft' ? 'Enter DD number' :
                        'Enter transaction reference number'
                      }
                      required disabled={isActive}
                      style={{ background: isActive ? '#f8fafc' : 'white' }} />
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Remarks</label>
                  <textarea className="form-input"
                    value={paymentForm.remarks}
                    onChange={(e) => setPaymentForm({ ...paymentForm, remarks: e.target.value })}
                    placeholder="Any additional notes" rows={2}
                    disabled={isActive}
                    style={{ background: isActive ? '#f8fafc' : 'white' }} />
                </div>

                {!isActive && (
                  <button type="submit" className="btn btn-primary" disabled={paymentLoading}>
                    <CreditCard size={14} />
                    {paymentLoading ? 'Saving...' : member.payment ? 'Update Payment' : 'Record Payment'}
                  </button>
                )}

                {member.payment && (
                  <div style={{
                    background: '#dcfce7', padding: '10px 14px', borderRadius: 8,
                    fontSize: 13, color: '#14532d', display: 'flex', alignItems: 'center', gap: 8
                  }}>
                    {/* DD/MM/YYYY format */}
                    <CheckCircle size={14} /> Payment recorded on {formatDate(member.payment.recordedAt)}
                  </div>
                )}
              </form>
            </div>
          ) : (
            <div className="card" style={{ padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <CreditCard size={17} style={{ color: 'var(--text-muted)' }} />
                <h3 style={{ fontSize: 15, color: 'var(--text-muted)' }}>Payment</h3>
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                Payment section will be available after both referrals approve.
              </div>
            </div>
          )}

          {/* Activate Membership Card */}
          {canActivate && (
            <div className="card" style={{
              padding: 24, border: '1px solid #bbf7d0', background: '#f0fdf4'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <CheckCircle size={18} style={{ color: '#16a34a' }} />
                <h3 style={{ fontSize: 15, color: '#15803d' }}>Activate Membership</h3>
              </div>
              <p style={{ fontSize: 13, color: '#166534', marginBottom: 16 }}>
                Payment has been recorded. Click below to activate this member's account.
                A welcome email will be sent automatically.
              </p>
              <button className="btn" onClick={handleActivate} disabled={activateLoading}
                style={{
                  background: '#16a34a', color: 'white',
                  width: '100%', justifyContent: 'center', padding: '12px'
                }}>
                <CheckCircle size={16} />
                {activateLoading ? 'Activating...' : 'Activate Account'}
              </button>
            </div>
          )}

          {isActive && (
            <div className="card" style={{
              padding: 20, border: '1px solid #bbf7d0', background: '#f0fdf4',
              display: 'flex', alignItems: 'center', gap: 12
            }}>
              <CheckCircle size={22} style={{ color: '#16a34a', flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 700, color: '#15803d', fontSize: 14 }}>Member is Active</div>
                <div style={{ fontSize: 12, color: '#166534', marginTop: 2 }}>
                  This member's account has been successfully activated.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
